<template>
  <div class="footer-main">
    <div class="footer-content">
      <!-- Column 1: Logo and Contact -->
      <div class="footer-col footer-col-about">
        <NuxtLink :to="`/${locale}`" class="footer-logo">
          <!-- Same custom logo as the header (per locale), white-text variant for the dark footer bg. -->
          <img
            v-if="locale === 'uk'"
            :src="config.site?.logoFooter ?? '/images/logo/logo-footer.svg'"
            :alt="config.site?.orgName ?? 'Школа Біблії'"
            class="footer-logo-img"
          />
          <img
            v-else
            src="/icons/new-logo-footer.svg"
            :alt="config.site?.orgName ?? 'Школа Біблії'"
            class="footer-logo-img"
          />
        </NuxtLink>
      <!--  <p class="footer-description">
          {{ description }}
        </p>-->
        <p class="footer-title sm">{{ t('layout.footer.main.phoneHours') }}</p>
        <div class="footer-contact">
          <div class="contact-item">
            <span>{{ t('layout.footer.main.phoneLabel') }}</span>
            <a :href="`tel:${phoneTel}`">{{ phone }}</a>
          </div>
          <div v-if="email" class="contact-item">
            <span>{{ t('layout.footer.main.emailLabel') }}</span>
            <a :href="`mailto:${email}`">{{ email }}</a>
          </div>
          <div class="contact-item">
            <span>{{ t('layout.footer.main.addressLabel') }}</span>
            <a :href="`https://maps.google.com/?q=${address}`" target="_blank">{{ address }}</a>
          </div>
        </div>
      </div>

      <!-- Column 2: Online Platform Links -->
      <!-- <div class="footer-col footer-col-links">
        <h6 class="footer-title">{{ t('layout.footer.main.platformTitle') }}</h6>
        <ul class="footer-links">
          <li v-for="link in platformLinks" :key="link.label">
            <NuxtLink :to="link.href">{{ link.label }}</NuxtLink>
          </li>
        </ul>
      </div> -->

      <!-- Column 3: Useful Links -->
      <div class="footer-col footer-col-links">
        <h6 class="footer-title">{{ t('layout.footer.main.usefulLinksTitle') }}</h6>
        <ul class="footer-links">
          <li v-for="link in usefulLinks" :key="link.label">
            <NuxtLink :to="link.href">{{ link.label }}</NuxtLink>
          </li>
          <li>
            <a href="https://v1.bibleawr.com/login" target="_blank" rel="noopener noreferrer">{{ t('layout.footer.main.teacherLogin') }}</a>
          </li>
        </ul>
      </div>

      <div class="footer-col footer-col-links">
      </div>

      <!-- Column 4: Social and Apps -->
      <div class="footer-col footer-col-social">

        <h6 class="footer-title">{{ t('layout.footer.main.socialNetworksTitle') }}</h6>
        <div class="footer-apps">
          <a
            v-for="app in appLinks"
            :key="app.name"
            :href="app.url"
            class="app-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
            :src="app.image"
            :alt="app.name"
            width="40"
            height="40"
            loading="lazy"
            decoding="async"
          >
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
interface FooterLink {
  label: string
  href: string
}

interface SocialLink {
  name: string
  url: string
  icon: string
}

interface AppLink {
  name: string
  url: string
  image: string
}

import { uaSocialLinks } from '@/config/defaults'

const { t, locale } = useI18n()
const config = useAppConfig()

const description = computed(() => t('layout.footer.main.description'))
// Site contacts from app.config.site (per locale), not hardcoded UA values.
const phone = computed(() => config.site?.phone ?? '0 800 30 20 20')
const phoneTel = computed(() => config.site?.phoneTel ?? phone.value)
const email = computed(() => (config.site?.email as string | null) ?? null)
const address = computed(() => t('layout.footer.main.address'))

const platformLinks = computed<FooterLink[]>(() => [
  { label: t('layout.footer.main.aboutUs'), href: `/${locale.value}/about` },
  { label: t('layout.footer.main.ourPrograms'), href: `/${locale.value}/courses` },
  { label: t('layout.footer.main.events'), href: `/${locale.value}` },
  { label: t('layout.footer.main.instructor'), href: `/${locale.value}/about` },
  { label: t('layout.footer.main.instructorDetails'), href: `/${locale.value}/about` },
  { label: t('layout.footer.main.admission'), href: `/sign-in` }
])

const usefulLinks = computed<FooterLink[]>(() => [
  { label: t('layout.footer.main.home'), href: `/${locale.value}` },
  { label: t('layout.footer.main.courses'), href: `/${locale.value}/courses` },
  { label: t('layout.footer.main.aboutUs'), href: `/${locale.value}/about` },
  { label: t('layout.footer.main.contactUs'), href: `/${locale.value}/contacts` },
])

const socialLinks: SocialLink[] = [
  { name: 'Facebook', url: 'https://www.facebook.com/probible', icon: 'fa-brands fa-facebook-f' },
  { name: 'Twitter', url: '#', icon: 'fa-brands fa-x-twitter' },
  { name: 'LinkedIn', url: '#', icon: 'fa-brands fa-linkedin-in' },
  { name: 'Instagram', url: 'https://www.instagram.com/school_bible', icon: 'fa-brands fa-instagram' },
  { name: 'Youtube', url: 'https://youtube.com/@bibleschool-onlinecourses', icon: 'fa-brands fa-youtube' },
  { name: 'TikTok', url: 'https://www.tiktok.com/@bible_school_ua', icon: 'fa-brands fa-tiktok' }
]

// Child sites (by/am/pl) set socialLinks via app.config → siteConfig.socials.
// UA flagship doesn't set socialLinks in app.config (Nuxt merges arrays across layers,
// which would duplicate icons) — falls back to uaSocialLinks from config/defaults.ts.
const appLinks = computed<AppLink[]>(() => {
  const configured = config.site?.socialLinks as AppLink[] | undefined
  return configured?.length ? configured : [...uaSocialLinks]
})
</script>

<style scoped>
.footer-main {
  background-color: var(--color-dark-grey);
  padding: 95px 0;
}
.footer-title.sm {
  font-size: 17px;
}
.footer-content {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: 3fr 2fr 2fr 3fr;
  gap: 40px;
  align-items: start;
}

.footer-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 27px;
}

.footer-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-bottom: 35px;
}

.footer-logo-img {
  display: block;
  width: 150px;
  height: auto;
}

.footer-description {
  font-family: var(--font-inter);
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-grey);
  margin-bottom: 25px;
}

.footer-contact {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-item {
  display: flex;
  gap: 10px;
  font-family: var(--font-inter);
  font-size: 14px;
  color: var(--color-grey);
}

.contact-item span {
  font-weight: 500;
  min-width: 60px;
}

.contact-item a {
  color: var(--color-grey);
  text-decoration: none;
  transition: color 0.3s ease;
}

.contact-item a:hover {
  color: var(--color-teal);
}

.footer-title {
  font-family: var(--font-inter);
  font-weight: semibold;
  font-size: 19px;
  margin-bottom: 20px;
  color: var(--button-active-text);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.footer-links li {
  list-style: none;
}

.footer-links a {
  font-family: var(--font-inter);
  font-size: 14px;
  font-weight: 500;
  color: var(--color-grey);
  text-decoration: none;
  display: inline-block;
  background-image: linear-gradient(var(--color-teal), var(--color-teal)), linear-gradient(var(--color-teal), var(--color-teal));
  background-size: 0% 1px, 0 1px;
  background-position: 100% 100%, 0 83%;
  background-repeat: no-repeat;
  transition: background-size 0.4s linear, color 0.4s linear;
  padding-bottom: 2px;
}

.footer-links a:hover {
  background-size: 0 1px, 100% 1px;
  color: var(--color-teal);
}

.footer-social {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 30px;
}

.social-link {
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  color: var(--button-active-text);
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-decoration: none;
  transition: all 0.3s ease;
}

.social-link:hover {
  border-color: var(--color-teal);
  background-color: var(--color-teal);
}

.social-link.facebook:hover {
  background-color: #1877F2;
  border-color: #1877F2;
}

.social-link.twitter:hover {
  background-color: #1DA1F2;
  border-color: #1DA1F2;
}

.social-link.linkedin:hover {
  background-color: #0A66C2;
  border-color: #0A66C2;
}

.social-link.instagram:hover {
  background-color: #E4405F;
  border-color: #E4405F;
}

.social-link.youtube:hover {
  background-color: #FF0000;
  border-color: #FF0000;
}

.social-link i {
  font-size: 16px;
}

.footer-apps {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
}

.app-link {
  display: inline-block;
  transition: transform 0.3s ease;

}

.app-link:hover {
  transform: translateY(-2px);
}

.app-link img {
  height: auto;
  width: 40px;
  height: 40px;
  border-radius: 5px;
}

@media (max-width: 1400px) {
  .footer-content {
    padding: 0 60px;
  }
}

@media (max-width: 991px) {
  .footer-main {
    padding: 60px 0;
  }

  .footer-content {
    grid-template-columns: 1fr 1fr;
    gap: 40px 30px;
    padding: 0 30px;
  }

  .footer-col-social {
    grid-column: 1 / -1;
  }

  .social-link {
    width: 30px;
    height: 30px;
  }

  .footer-social {
    gap: 10px;
  }
}

@media (max-width: 767px) {

  .footer-content {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 0 20px;
  }

  .footer-col-social {
    grid-column: 1;
  }
}

@media (max-width: 575px) {
  .footer-social {
    gap: 10px;
  }
}
</style>
